import { useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { type Query as TCubeQuery } from "@cubejs-client/core"
import type { PaginationState } from "@tanstack/react-table"

import Alert from "~/ui-rtk/components/ui/common/Alert"
import { Loader } from "~/ui-rtk/components/ui/common"
import { TableVisualization } from "~/ui-rtk/components/ui/charts"
import { H2, P } from "~/ui-rtk/components/ui/typography"
import ComboboxInput, {
  type TComboboxOption,
} from "~/ui-rtk/components/ui/controls/ComboboxInput"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import { toCurrency } from "~/ui-rtk/utils/number-utils"
import type {
  TMetric,
  TableVisualizationColumn,
} from "~/ui-rtk/constants/table-visualization-types"

const REPORTS_CUBE = "company_experiment_reports"

// Metric filters produced by the incrementality pipeline, e.g.
// "baseline_revenue" -> "Baseline Revenue"
const prettifyMetric = (value: string) =>
  value
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")

const formatCount = (value: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(value)

const formatPct = (value: number) =>
  `${value >= 0 ? "+" : ""}${value.toFixed(1)}%`

const RESULT_COLUMNS: TableVisualizationColumn[] = [
  {
    key: "target_metric",
    type: "title",
    header: "Target Metric",
    width: 200,
  },
  {
    key: "observed",
    type: "number",
    header: "Observed",
    width: 140,
    render: (row: TMetric) => (
      <div>
        {row.is_sessions
          ? formatCount(row.observed as number)
          : toCurrency(row.observed as number)}
      </div>
    ),
  },
  {
    key: "counterfactual",
    type: "number",
    header: "Counterfactual",
    width: 140,
    render: (row: TMetric) => (
      <div>
        {row.is_sessions
          ? formatCount(row.counterfactual as number)
          : toCurrency(row.counterfactual as number)}
      </div>
    ),
  },
  {
    key: "lift",
    type: "number",
    header: "Incremental Lift",
    width: 150,
    render: (row: TMetric) => (
      <div>
        {row.is_sessions
          ? formatCount(row.lift as number)
          : toCurrency(row.lift as number)}
      </div>
    ),
  },
  {
    key: "pct_lift",
    type: "percent",
    header: "% Lift (95% CI)",
    width: 200,
    render: (row: TMetric) => (
      <div className="flex flex-col">
        <span>{formatPct(row.pct_lift as number)}</span>
        <span className="text-xs text-basic-grey-inactive">
          {formatPct(row.pct_lift_lower as number)} to{" "}
          {formatPct(row.pct_lift_upper as number)}
        </span>
      </div>
    ),
  },
  {
    key: "total_spend",
    type: "currency",
    header: "Experiment Spend",
    width: 150,
  },
  {
    key: "incremental_roas",
    type: "coef",
    header: "Incremental ROAS",
    width: 150,
    render: (row: TMetric) => (
      <div>
        {row.is_sessions || !(row.total_spend as number)
          ? "—"
          : `${(row.incremental_roas as number).toFixed(2)}x`}
      </div>
    ),
  },
]

function ResultsTable({ experimentName }: { experimentName: string }) {
  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  })

  const { resultSet, isLoading, error } = useCubeQuery({
    dimensions: [`${REPORTS_CUBE}.revenue_type`, `${REPORTS_CUBE}.effect_type`],
    measures: [
      `${REPORTS_CUBE}.current_revenue`,
      `${REPORTS_CUBE}.total_sessions`,
      `${REPORTS_CUBE}.counterfactual`,
      `${REPORTS_CUBE}.lift`,
      `${REPORTS_CUBE}.pct_lift`,
      `${REPORTS_CUBE}.pct_lift_lower`,
      `${REPORTS_CUBE}.pct_lift_upper`,
      `${REPORTS_CUBE}.total_spend`,
      `${REPORTS_CUBE}.incremental_roas`,
    ],
    filters: [
      {
        member: `${REPORTS_CUBE}.experiment_name`,
        operator: "equals",
        values: [experimentName],
      },
    ],
  } satisfies TCubeQuery)

  const rows = useMemo(() => {
    const prefix = `${REPORTS_CUBE}.`
    return (resultSet?.tablePivot() ?? []).map(raw => {
      const r: Record<string, unknown> = {}
      Object.entries(raw).forEach(([key, value]) => {
        r[key.startsWith(prefix) ? key.slice(prefix.length) : key] = value
      })

      const revenueType = String(r.revenue_type ?? "")
      const isSessions = revenueType.endsWith("sessions")

      return {
        id: `${revenueType}_${r.effect_type}`,
        target_metric: prettifyMetric(revenueType),
        is_sessions: isSessions,
        observed: Number(isSessions ? r.total_sessions : r.current_revenue),
        counterfactual: Number(r.counterfactual),
        lift: Number(r.lift),
        // The reports view stores pct_lift as a fraction (0.05 = 5%): the
        // pipeline computes `observed / counterfactual - 1` (make_reports in
        // incrementality/scripts/modules/sdid_utils.py), so scale for display.
        pct_lift: Number(r.pct_lift) * 100,
        pct_lift_lower: Number(r.pct_lift_lower) * 100,
        pct_lift_upper: Number(r.pct_lift_upper) * 100,
        total_spend: Number(r.total_spend),
        incremental_roas: Number(r.incremental_roas),
      }
    })
  }, [resultSet])

  if (isLoading) {
    return <Loader />
  }

  if (error) {
    return (
      <Alert variant="danger" content="Failed to load experiment results" />
    )
  }

  if (!rows.length) {
    return (
      <P className="text-sm text-basic-grey-inactive">
        No results available yet for this experiment. Results are produced by
        the daily incrementality analysis once the experiment is active.
      </P>
    )
  }

  return (
    <TableVisualization
      metrics={rows}
      columnConfig={RESULT_COLUMNS}
      chartId="experiment-results-table"
      compareUniqueKey="id"
      paginationState={paginationState}
      setPaginationState={setPaginationState}
      hideItemsPerPage={true}
    />
  )
}

export default function ExperimentResults() {
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedName = searchParams.get("name") ?? ""

  // Every experiment with published results, straight from the reports view.
  const {
    resultSet: namesResultSet,
    isLoading: namesLoading,
    error: namesError,
  } = useCubeQuery({
    dimensions: [`${REPORTS_CUBE}.experiment_name`],
  } satisfies TCubeQuery)

  const experimentOptions: TComboboxOption[] = useMemo(() => {
    const names = (namesResultSet?.tablePivot() ?? [])
      .map(row => String(row[`${REPORTS_CUBE}.experiment_name`] ?? ""))
      .filter(Boolean)
      .sort()
    return names.map((name, index) => ({ id: index, label: name, value: name }))
  }, [namesResultSet])

  const handleSelect = (name: string) => {
    setSearchParams(params => {
      params.set("name", name)
      return params
    })
  }

  return (
    <div className="flex h-full flex-col gap-6 bg-basic-black px-4 py-4 text-basic-light">
      <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="grid gap-1">
          <H2 className="text-white">Experiment Results</H2>
          <P className="text-sm text-basic-grey-inactive">
            Incremental lift, counterfactual, and iROAS per target metric,
            estimated by Bayesian Synthetic Control.
          </P>
        </div>
        <div className="w-full md:w-90">
          <ComboboxInput
            value={selectedName}
            onChange={handleSelect}
            options={experimentOptions}
            placeholder="Select an experiment"
          />
        </div>
      </header>

      <section>
        {namesLoading && <Loader />}
        {namesError && (
          <Alert variant="danger" content="Failed to load experiments" />
        )}
        {!namesLoading && !namesError && !experimentOptions.length && (
          <P className="text-sm text-basic-grey-inactive">
            No experiment results found for this company yet.
          </P>
        )}
        {selectedName && <ResultsTable experimentName={selectedName} />}
        {!selectedName && !!experimentOptions.length && (
          <P className="text-sm text-basic-grey-inactive">
            Select an experiment above to see its results.
          </P>
        )}
      </section>
    </div>
  )
}
